import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Tooltip,
  TablePagination,
  alpha,
  useTheme,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  PendingActions as PendingActionsIcon,
  Done as DoneIcon,
  MonetizationOn as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AddCard as ReplenishIcon,
  SwapHoriz as TransferIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axiosInstance from '../services/axiosInstance';
import { Custody } from '../types/custodies.types';
import { custodiesService } from '../services/custodies.service';
import CustodyDialog from '../components/custodies/CustodyDialog';
import CustodyDetailsDialog from '../components/custodies/CustodyDetailsDialog';
import CustodyReplenishmentDialog from '../components/custodies/CustodyReplenishmentDialog';
import { useLanguage } from '../contexts/LanguageContext';
import { useAppSelector } from '../hooks/redux.hooks';
import { useConfirm } from '../hooks/useConfirm';

const translations = {
  en: {
    title: 'Custodies Management',
    newCustody: 'New Custody',
    status: 'Status',
    allStatuses: 'All Statuses',
    open: 'Open',
    settled: 'Settled',
    closed: 'Closed',
    custodyNumber: 'Custody Number',
    employee: 'Employee',
    branch: 'Branch',
    initialAmount: 'Initial Amount',
    currentBalance: 'Current Balance',
    custodyDate: 'Date',
    actions: 'Actions',
    view: 'View Details',
    edit: 'Edit',
    settle: 'Settle',
    delete: 'Delete',
    search: 'Search by number, employee, or branch...',
    loading: 'Loading...',
    noData: 'No custodies found',
    refresh: 'Refresh',
    totalCustodies: 'Total Custodies',
    openCustodies: 'Open',
    settledCustodies: 'Settled',
    rowsPerPage: 'Rows per page:',
    deleteConfirm: 'Are you sure you want to delete this custody?',
    settleConfirm: 'Are you sure you want to settle this custody?',
    deleteSuccess: 'Custody deleted successfully',
    settleSuccess: 'Custody settled successfully',
    deleteFailed: 'Failed to delete custody',
    settleFailed: 'Failed to settle custody',
    kwd: 'KWD',
    replenish: 'Replenish',
    replenishSuccess: 'Replenishment added successfully',
    noPermission: 'You do not have permission for this action',
  },
  ar: {
    title: 'إدارة العهد النقدية',
    newCustody: 'عهدة جديدة',
    status: 'الحالة',
    allStatuses: 'جميع الحالات',
    open: 'مفتوحة',
    settled: 'مسواة',
    closed: 'مغلقة',
    custodyNumber: 'رقم العهدة',
    employee: 'الموظف',
    branch: 'الفرع',
    initialAmount: 'المبلغ الأولي',
    currentBalance: 'الرصيد الحالي',
    custodyDate: 'التاريخ',
    actions: 'الإجراءات',
    view: 'عرض التفاصيل',
    edit: 'تعديل',
    settle: 'تسوية',
    delete: 'حذف',
    search: 'البحث برقم العهدة، الموظف، أو الفرع...',
    loading: 'جاري التحميل...',
    noData: 'لا توجد عهد',
    refresh: 'تحديث',
    totalCustodies: 'إجمالي العهد',
    openCustodies: 'مفتوحة',
    settledCustodies: 'مسواة',
    rowsPerPage: 'عدد الصفوف:',
    deleteConfirm: 'هل أنت متأكد من حذف هذه العهدة؟',
    settleConfirm: 'هل أنت متأكد من تسوية هذه العهدة؟',
    deleteSuccess: 'تم حذف العهدة بنجاح',
    settleSuccess: 'تم تسوية العهدة بنجاح',
    deleteFailed: 'فشل حذف العهدة',
    settleFailed: 'فشل تسوية العهدة',
    kwd: 'د.ك',
    replenish: 'تعزيز',
    replenishSuccess: 'تم تعزيز العهدة بنجاح',
    noPermission: 'ليس لديك صلاحية لهذا الإجراء',
  },
};

const CustodiesPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const confirm = useConfirm();

  // ✅ الصلاحيات من Redux بدل localStorage
  const user = useAppSelector((state) => state.auth.user);
  const permissions = useAppSelector((state) => state.auth.permissions);

  // ✅ دالة فحص الصلاحية
  const hasPermission = (perm: string): boolean => {
    if (user?.isSuperAdmin) return true;
    return permissions.includes(perm);
  };

  const [custodies, setCustodies] = useState<Custody[]>([]);
  const [filteredCustodies, setFilteredCustodies] = useState<Custody[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedCustody, setSelectedCustody] = useState<Custody | null>(null);
  const [editCustody, setEditCustody] = useState<Custody | null>(null);
  const [replenishDialogOpen, setReplenishDialogOpen] = useState(false);
  const [replenishCustody, setReplenishCustody] = useState<Custody | null>(null);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferCustody, setTransferCustody] = useState<Custody | null>(null);
  const [transferEmployeeId, setTransferEmployeeId] = useState<number | null>(null);
  const [transferNotes, setTransferNotes] = useState('');
  const [allEmployees, setAllEmployees] = useState<any[]>([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    settled: 0,
  });

  useEffect(() => {
    loadCustodies();
    axiosInstance.get('/Employees').then(res => setAllEmployees(res.data?.data || res.data || [])).catch(() => {});
  }, [statusFilter]);

  useEffect(() => {
    applyFilters();
    calculateStats();
  }, [searchTerm, custodies]);

  const loadCustodies = async () => {
    setLoading(true);
    try {
      const response = await custodiesService.getAll(statusFilter || undefined);
      setCustodies(response);
    } catch (error: any) {
      console.error('Failed to load custodies:', error);
      // إذا كان 403 = ليس لديه صلاحية
      if (error.response?.status === 403) {
        toast.error(t.noPermission, {
          position: 'top-center',
          autoClose: 3000,
          style: { fontSize: '16px', fontWeight: 'bold' }
        });
      } else {
        toast.error(t.loading, { 
          position: 'top-center', 
          autoClose: 3000,
          style: { fontSize: '16px', fontWeight: 'bold' }
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (!searchTerm.trim()) {
      setFilteredCustodies(custodies);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = custodies.filter((custody) =>
      custody.custodyNumber.toLowerCase().includes(term) ||
      custody.employeeName?.toLowerCase().includes(term) ||
      custody.employeeNameAr?.toLowerCase().includes(term) ||
      custody.branchName?.toLowerCase().includes(term) ||
      custody.branchNameAr?.toLowerCase().includes(term)
    );
    setFilteredCustodies(filtered);
    setPage(0);
  };

  const calculateStats = () => {
    const total = custodies.length;
    const open = custodies.filter((c) => c.status === 'Open').length;
    const settled = custodies.filter((c) => c.status === 'Settled').length;
    setStats({ total, open, settled });
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({ message: t.deleteConfirm, severity: 'error' });
    if (!ok) return;
    try {
      await custodiesService.delete(id);
      toast.success(t.deleteSuccess, {
        position: 'top-center',
        autoClose: 2000,
        style: { fontSize: '16px', fontWeight: 'bold' }
      });
      loadCustodies();
    } catch (error: any) {
      let errorMessage = t.deleteFailed;
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }

      toast.error(errorMessage, {
        position: 'top-center',
        autoClose: 5000,
        style: { fontSize: '16px', fontWeight: 'bold' }
      });
    }
  };

  const handleAdd = () => {
    setEditCustody(null);
    setDialogOpen(true);
  };

  const handleEdit = async (custody: Custody) => {
    setEditCustody(custody);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditCustody(null);
  };

  const handleTransfer = async () => {
    if (!transferCustody || !transferEmployeeId) return;
    try {
      await axiosInstance.post(`/Custodies/${transferCustody.custodyId}/transfer-responsibility`, {
        newEmployeeId: transferEmployeeId, notes: transferNotes
      });
      toast.success(isRTL ? 'تم تحويل المسؤولية بنجاح' : 'Responsibility transferred successfully');
      setTransferDialogOpen(false);
      loadCustodies();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error');
    }
  };

  const handlePrintHandover = () => {
    if (!transferCustody) return;
    const oldEmp = transferCustody.employeeName || '';
    const newEmp = allEmployees.find(e => e.employeeId === transferEmployeeId);
    const w = window.open('', '_blank', 'width=800,height=600');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head><meta charset="UTF-8">
<title>${isRTL ? 'محضر تسليم عهدة' : 'Custody Handover Report'}</title>
<style>
  body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; direction: ${isRTL ? 'rtl' : 'ltr'}; }
  .header { text-align: center; border-bottom: 3px solid #1565c0; padding-bottom: 15px; margin-bottom: 25px; }
  .header h2 { color: #1565c0; margin: 5px 0; }
  .title { text-align: center; margin-bottom: 25px; }
  .title h3 { color: #1565c0; border: 2px solid #1565c0; display: inline-block; padding: 8px 30px; border-radius: 8px; }
  table { width: 100%; border-collapse: collapse; margin: 15px 0; }
  td { padding: 10px 15px; border: 1px solid #ddd; font-size: 13px; }
  td:first-child { font-weight: bold; background: #e3f2fd; width: 35%; }
  .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
  .sig-box { text-align: center; width: 30%; }
  .sig-line { border-top: 1px solid #333; margin-top: 60px; padding-top: 8px; font-weight: bold; }
  .date { text-align: ${isRTL ? 'left' : 'right'}; font-size: 11px; color: #999; margin-bottom: 10px; }
  @media print { body { padding: 20px; } }
</style></head><body>
<div class="date">${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div>
<div class="header"><h2>${isRTL ? 'محضر تسليم عهدة' : 'Custody Handover Report'}</h2></div>
<div class="title"><h3>${isRTL ? 'تحويل مسؤولية العهدة' : 'Transfer of Custody Responsibility'}</h3></div>
<table>
  <tr><td>${isRTL ? 'رقم العهدة' : 'Custody No.'}</td><td>${transferCustody.custodyNumber}</td></tr>
  <tr><td>${isRTL ? 'الفرع' : 'Branch'}</td><td>${transferCustody.branchName || '-'}</td></tr>
  <tr><td>${isRTL ? 'المبلغ الأساسي' : 'Initial Amount'}</td><td>${transferCustody.initialAmount.toFixed(3)} KD</td></tr>
  <tr><td>${isRTL ? 'الرصيد الحالي' : 'Current Balance'}</td><td style="font-weight:bold;color:${transferCustody.currentBalance < 0 ? 'red' : 'green'}">${transferCustody.currentBalance.toFixed(3)} KD</td></tr>
  <tr><td>${isRTL ? 'المسؤول السابق' : 'Previous Holder'}</td><td>${oldEmp}</td></tr>
  <tr><td>${isRTL ? 'المسؤول الجديد' : 'New Holder'}</td><td>${newEmp ? (isRTL ? newEmp.fullNameAr : newEmp.fullName) : ''}</td></tr>
  <tr><td>${isRTL ? 'تاريخ التحويل' : 'Transfer Date'}</td><td>${new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</td></tr>
  ${transferNotes ? `<tr><td>${isRTL ? 'ملاحظات' : 'Notes'}</td><td>${transferNotes}</td></tr>` : ''}
</table>
<div class="signatures">
  <div class="sig-box"><div class="sig-line">${isRTL ? 'المسؤول السابق' : 'Previous Holder'}</div></div>
  <div class="sig-box"><div class="sig-line">${isRTL ? 'المسؤول الجديد' : 'New Holder'}</div></div>
  <div class="sig-box"><div class="sig-line">${isRTL ? 'رئيس الحسابات' : 'Chief Accountant'}</div></div>
</div></body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 500);
  };

  const handleView = async (id: number) => {
    try {
      const response = await custodiesService.getById(id);
      setSelectedCustody(response);
      setDetailsDialogOpen(true);
    } catch (error) {
      console.error('Failed to load custody details:', error);
    }
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedCustody(null);
  };

  const handleSettle = async (id: number) => {
    const ok = await confirm({ message: t.settleConfirm, severity: 'warning' });
    if (!ok) return;
    try {
      await custodiesService.settle(id, {
        custodyId: id,
        settlementDate: new Date().toISOString(),
      });
      toast.success(t.settleSuccess, {
        position: 'top-center',
        autoClose: 2000,
        style: { fontSize: '16px', fontWeight: 'bold' }
      });
      loadCustodies();
    } catch (error: any) {
      let errorMessage = t.settleFailed;
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }

      toast.error(errorMessage, {
        position: 'top-center',
        autoClose: 3000,
        style: { fontSize: '16px', fontWeight: 'bold' }
      });
    }
  };

  const handleReplenish = (custody: Custody) => {
    setReplenishCustody(custody);
    setReplenishDialogOpen(true);
  };
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedCustodies = filteredCustodies.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getStatusColor = (status?: string): 'default' | 'success' | 'info' | 'warning' => {
    switch (status) {
      case 'Open':
        return 'info';
      case 'Settled':
        return 'success';
      case 'Closed':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status?: string): string => {
    switch (status) {
      case 'Open':
        return t.open;
      case 'Settled':
        return t.settled;
      case 'Closed':
        return t.closed;
      default:
        return status || '';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ar' ? 'ar-KW' : 'en-GB');
  };

  const formatCurrency = (amount: number) => {
    return `${amount.toFixed(3)} ${t.kwd}`;
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Statistics Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 2.5, mb: 3 }}>
        <Card 
          elevation={6} 
          sx={{ 
            background: isDarkMode 
              ? 'linear-gradient(135deg, #0f3460 0%, #16213e 100%)' 
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            color: 'white', 
            transition: 'all 0.3s ease',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            '&:hover': { 
              transform: 'translateY(-8px)',
              boxShadow: '0 12px 24px rgba(102, 126, 234, 0.4)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
              pointerEvents: 'none',
            }
          }}
        >
          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5, fontWeight: 500 }}>
                  {t.totalCustodies}
                </Typography>
                <Typography variant="h3" fontWeight="bold" sx={{ mb: 0.5 }}>
                  {stats.total}
                </Typography>
              </Box>
              <Box sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                borderRadius: 2, 
                p: 1.5,
                backdropFilter: 'blur(10px)',
              }}>
                <AccountBalanceIcon sx={{ fontSize: 40 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card 
          elevation={6} 
          sx={{ 
            background: isDarkMode 
              ? 'linear-gradient(135deg, #1565c0 0%, #0277bd 100%)' 
              : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
            color: 'white', 
            transition: 'all 0.3s ease',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            '&:hover': { 
              transform: 'translateY(-8px)',
              boxShadow: '0 12px 24px rgba(79, 172, 254, 0.4)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
              pointerEvents: 'none',
            }
          }}
        >
          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5, fontWeight: 500 }}>
                  {t.openCustodies}
                </Typography>
                <Typography variant="h3" fontWeight="bold" sx={{ mb: 0.5 }}>
                  {stats.open}
                </Typography>
              </Box>
              <Box sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                borderRadius: 2, 
                p: 1.5,
                backdropFilter: 'blur(10px)',
              }}>
                <PendingActionsIcon sx={{ fontSize: 40 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card 
          elevation={6} 
          sx={{ 
            background: isDarkMode 
              ? 'linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)' 
              : 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', 
            color: 'white', 
            transition: 'all 0.3s ease',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            '&:hover': { 
              transform: 'translateY(-8px)',
              boxShadow: '0 12px 24px rgba(67, 233, 123, 0.4)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
              pointerEvents: 'none',
            }
          }}
        >
          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5, fontWeight: 500 }}>
                  {t.settledCustodies}
                </Typography>
                <Typography variant="h3" fontWeight="bold" sx={{ mb: 0.5 }}>
                  {stats.settled}
                </Typography>
              </Box>
              <Box sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                borderRadius: 2, 
                p: 1.5,
                backdropFilter: 'blur(10px)',
              }}>
                <DoneIcon sx={{ fontSize: 40 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Header & Actions */}
      <Paper 
        elevation={4} 
        sx={{ 
          p: 2.5, 
          mb: 3, 
          borderRadius: 3,
          background: isDarkMode 
            ? alpha(theme.palette.background.paper, 0.6)
            : 'white',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              bgcolor: isDarkMode 
                ? alpha(theme.palette.primary.main, 0.2)
                : alpha(theme.palette.primary.main, 0.1),
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
            }}>
              <MoneyIcon sx={{ fontSize: 32, color: '#667eea' }} />
            </Box>
            <Typography variant="h5" fontWeight="bold" sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              {t.title}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Tooltip title={t.refresh} arrow>
              <IconButton
                color="primary"
                onClick={loadCustodies}
                sx={{
                  bgcolor: isDarkMode 
                    ? alpha(theme.palette.primary.main, 0.2)
                    : alpha(theme.palette.primary.main, 0.1),
                  '&:hover': { 
                    bgcolor: isDarkMode 
                      ? alpha(theme.palette.primary.main, 0.3)
                      : alpha(theme.palette.primary.main, 0.2),
                    transform: 'rotate(180deg)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {/* ✅ زر الإضافة يظهر فقط لمن لديه صلاحية CUSTODY_CREATE */}
            {hasPermission('CUSTODY_CREATE') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                size="large"
                sx={{
                  borderRadius: 2,
                  px: 3,
                  background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                  boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)',
                    boxShadow: '0 5px 8px 3px rgba(102, 126, 234, .4)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {t.newCustody}
              </Button>
            )}
          </Stack>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto' }, gap: 2 }}>
          <TextField
            fullWidth
            placeholder={t.search}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#667eea' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 0 0 2px rgba(102, 126, 234, 0.1)',
                },
                '&.Mui-focused': {
                  boxShadow: '0 0 0 2px rgba(102, 126, 234, 0.2)',
                },
              },
            }}
          />
          <TextField
            select
            label={t.status}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
            sx={{
              minWidth: 200,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          >
            <MenuItem value="">{t.allStatuses}</MenuItem>
            <MenuItem value="Open">{t.open}</MenuItem>
            <MenuItem value="Settled">{t.settled}</MenuItem>
            <MenuItem value="Closed">{t.closed}</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer 
        component={Paper} 
        elevation={4} 
        sx={{ 
          borderRadius: 3, 
          overflow: 'hidden',
          background: isDarkMode 
            ? alpha(theme.palette.background.paper, 0.6)
            : 'white',
          backdropFilter: 'blur(10px)',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
            <CircularProgress sx={{ color: '#667eea' }} size={60} />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  background: isDarkMode 
                    ? 'linear-gradient(135deg, #0f3460 0%, #16213e 100%)' 
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 2 }}>{t.custodyNumber}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 2 }}>{t.employee}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 2 }}>{t.branch}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 2 }} align="right">{t.initialAmount}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 2 }} align="right">{t.currentBalance}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 2 }}>{t.custodyDate}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 2 }} align="center">{t.status}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 2 }} align="center">{t.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCustodies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <AccountBalanceIcon sx={{ fontSize: 64, color: 'text.disabled', opacity: 0.3 }} />
                      <Typography variant="h6" color="text.secondary">
                        {t.noData}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCustodies.map((custody, index) => (
                  <TableRow
                    key={custody.custodyId}
                    hover
                    sx={{
                      '&:hover': {
                        bgcolor: isDarkMode 
                          ? alpha(theme.palette.primary.main, 0.1)
                          : 'rgba(102, 126, 234, 0.05)',
                      },
                      transition: 'background-color 0.2s ease',
                      bgcolor: index % 2 === 0 
                        ? (isDarkMode ? 'transparent' : 'rgba(0,0,0,0.02)')
                        : 'transparent',
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                        }}>
                          <Typography fontWeight="bold" color="primary" variant="body2">
                            {custody.custodyNumber}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {isRTL ? (custody.employeeNameAr || custody.employeeName) : custody.employeeName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {custody.branchName ? (isRTL ? custody.branchNameAr : custody.branchName) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        <Typography fontWeight="bold" color="success.main">
                          {formatCurrency(custody.initialAmount || 0)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {custody.currentBalance < 0 ? (
                            <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
                          ) : (
                            <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          )}
                          <Typography
                            fontWeight="bold"
                            color={custody.currentBalance < 0 ? 'error.main' : 'success.main'}
                          >
                            {formatCurrency(custody.currentBalance)}
                          </Typography>
                        </Box>
                        {custody.currentBalance < 0 && (
                          <Chip label={isRTL ? `مستحق للموظف: ${formatCurrency(Math.abs(custody.currentBalance))}` : `Owed to employee: ${formatCurrency(Math.abs(custody.currentBalance))}`} color="error" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {custody.custodyDate ? formatDate(custody.custodyDate) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                        <Chip
                          label={getStatusLabel(custody.status)}
                          color={getStatusColor(custody.status)}
                          size="small"
                        sx={{
                          fontWeight: 'bold',
                          borderRadius: 1.5,
                          minWidth: 80,
                        }}
                      />
                        {custody.status === 'Open' && custody.employeeBranchId && custody.branchId && custody.employeeBranchId !== custody.branchId && (
                          <Tooltip title={isRTL ? `الفرع الحالي للموظف: ${custody.employeeBranchName}` : `Employee current branch: ${custody.employeeBranchName}`}>
                            <Chip label={isRTL ? `منقول — ${custody.employeeBranchName}` : `Transferred — ${custody.employeeBranchName}`} color="warning" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {/* ✅ العرض متاح لمن لديه CUSTODY_VIEW */}
                        <Tooltip title={t.view} arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleView(custody.custodyId)}
                            sx={{
                              color: 'primary.main',
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                transform: 'scale(1.1)',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* Transfer Responsibility */}
                        {custody.status === 'Open' && hasPermission('CUSTODY_EDIT') && (
                          <Tooltip title={isRTL ? 'تحويل المسؤولية' : 'Transfer Responsibility'} arrow>
                            <IconButton size="small" onClick={() => { setTransferCustody(custody); setTransferEmployeeId(null); setTransferNotes(''); setTransferDialogOpen(true); }}
                              sx={{ color: 'warning.main', bgcolor: alpha(theme.palette.warning.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.2), transform: 'scale(1.1)' }, transition: 'all 0.2s ease' }}>
                              <TransferIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* ✅ التعديل يظهر فقط لمن لديه CUSTODY_EDIT */}
                        {custody.status === 'Open' && hasPermission('CUSTODY_EDIT') && (
                          <Tooltip title={t.edit} arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(custody)}
                              sx={{
                                color: 'info.main',
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.info.main, 0.2),
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* ✅ الحذف يظهر فقط لمن لديه CUSTODY_DELETE */}
                        {custody.status === 'Open' && hasPermission('CUSTODY_DELETE') && (
                          <Tooltip title={t.delete} arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(custody.custodyId)}
                              sx={{
                                color: 'error.main',
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.error.main, 0.2),
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* ✅ تعزيز العهدة */}
                        {custody.status === 'Open' && hasPermission('CUSTODY_EDIT') && (
                          <Tooltip title={t.replenish} arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleReplenish(custody)}
                              sx={{
                                color: '#1976d2',
                                bgcolor: alpha('#1976d2', 0.1),
                                '&:hover': {
                                  bgcolor: alpha('#1976d2', 0.2),
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <ReplenishIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* ✅ التسوية تظهر فقط لمن لديه CUSTODY_SETTLE */}
                        {custody.status === 'Open' && hasPermission('CUSTODY_SETTLE') && (
                          <Tooltip title={t.settle} arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleSettle(custody.custodyId)}
                              sx={{
                                color: 'success.main',
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.success.main, 0.2),
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50]}
          component="div"
          count={filteredCustodies.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t.rowsPerPage}
          labelDisplayedRows={({ from, to, count }) =>
            isRTL ? `${from}-${to} من ${count}` : `${from}-${to} of ${count}`
          }
          sx={{
            borderTop: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            bgcolor: isDarkMode 
              ? alpha(theme.palette.background.paper, 0.4)
              : 'rgba(0,0,0,0.02)',
          }}
        />
      </TableContainer>

      <CustodyDialog open={dialogOpen} onClose={handleCloseDialog} onSave={loadCustodies} custody={editCustody} />
      <CustodyDetailsDialog 
        open={detailsDialogOpen} 
        onClose={handleCloseDetails} 
        custody={selectedCustody} 
        onEdit={handleEdit}
        onDeleted={loadCustodies}
      />
      <CustodyReplenishmentDialog
        open={replenishDialogOpen}
        onClose={() => { setReplenishDialogOpen(false); setReplenishCustody(null); }}
        custody={replenishCustody}
        onSave={() => { loadCustodies(); toast.success(t.replenishSuccess, { position: 'top-center', style: { fontSize: '16px', fontWeight: 'bold' } }); }}
      />

      {/* Transfer Responsibility Dialog */}
      <Dialog open={transferDialogOpen} onClose={() => setTransferDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TransferIcon />
            {isRTL ? 'تحويل مسؤولية العهدة' : 'Transfer Custody Responsibility'}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label={isRTL ? 'رقم العهدة' : 'Custody No.'} value={transferCustody?.custodyNumber || ''} disabled size="small" />
            <TextField label={isRTL ? 'المسؤول الحالي' : 'Current Holder'} value={transferCustody?.employeeName || ''} disabled size="small" />
            <TextField label={isRTL ? 'الرصيد الحالي' : 'Current Balance'} value={`${transferCustody?.currentBalance?.toFixed(3) || '0.000'} KD`} disabled size="small"
              sx={{ '& input': { color: (transferCustody?.currentBalance || 0) < 0 ? 'red' : 'green', fontWeight: 'bold' } }} />
            <Autocomplete
              options={allEmployees.filter((e: any) => e.employeeId !== transferCustody?.employeeId)}
              getOptionLabel={(option: any) => `${option.employeeCode} - ${isRTL ? option.fullNameAr : option.fullName}`}
              value={allEmployees.find((e: any) => e.employeeId === transferEmployeeId) || null}
              onChange={(_, newValue: any) => setTransferEmployeeId(newValue?.employeeId || null)}
              renderInput={(params) => <TextField {...params} label={isRTL ? 'المسؤول الجديد' : 'New Holder'} size="small" required />}
              size="small"
            />
            <TextField label={isRTL ? 'ملاحظات' : 'Notes'} value={transferNotes} onChange={e => setTransferNotes(e.target.value)} multiline rows={2} size="small" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setTransferDialogOpen(false)}>{isRTL ? 'إلغاء' : 'Cancel'}</Button>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrintHandover} disabled={!transferEmployeeId}>
            {isRTL ? 'طباعة محضر التسليم' : 'Print Handover'}
          </Button>
          <Button variant="contained" color="warning" startIcon={<TransferIcon />} onClick={handleTransfer} disabled={!transferEmployeeId}>
            {isRTL ? 'تحويل المسؤولية' : 'Transfer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustodiesPage;